import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useCollection from 'Hooks/useCollection';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import {
  QueryVal, removeQuery, addQuery,
} from 'Helpers/Query';
import { goalId, createNewGoal, goalType } from 'Consts/Queries';
import GoalFormOverride from 'Resources/GoalForm';
import ReadOnlyGoalOverride from 'Resources/ReadOnlyGoalForm';
import { useBeaconDispatch } from 'Context/BeaconContext';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { FDPrimaryActionButton, FDHeadlineText, FDStack } from '@facet-wealth/design';
import useScrollTopOnMount from 'Hooks/useScrollTopOnMount';
import GoalForm from './GoalForm';
import MyGoalsSidebar from './MyGoalsSidebar';
import GoalTypeSelector from './GoalTypeSelector';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flex: '1',
    alignItems: 'center',
    flexDirection: 'column',
    padding: theme.spacing(6),
  },
  goalDetailContainer: {
    display: 'flex',
    width: '100%',
    maxWidth: theme.dimensions.xxl,
  },
  goalSidebar: {},
  goalDetail: {
    width: '100%',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.facet.border}`,
  },
  mobileSidebar: {
    width: '100%',
    backgroundColor: '#20485D',
  },
  addGoalButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(6),
  },
  selectGoalHeader: {
    flexDirection: 'column',
  },
  goalHeaderSubtitle: {
    marginTop: theme.spacing(2),
  },
  [theme.breakpoints.between('sm', 'md')]: {
    root: {
      padding: theme.spacing(2),
    },
    goalDetailContainer: {},
  },
  [theme.breakpoints.down('sm')]: {
    root: {
      padding: 0,
    },
    goalDetailContainer: {
      flexDirection: 'column',
    },
    addGoalButtonContainer: {
      flexDirection: 'column',
      marginLeft: '0',
    },
    goalHeaderSpacer: {
      display: 'none',
    },
  },
}));

const Goals = () => {
  useScrollTopOnMount();
  const classes = useStyles();
  const createNew = QueryVal(createNewGoal);
  const activeGoalId = QueryVal(goalId);
  const selectedGoalType = QueryVal(goalType);
  const {
    rootNodeProps,
    logEvent,
    logEventSuccess,
    logEventFailureWithError,
  } = useEventTrackingLogEvent({
    nodeName: 'Client Goals Page',
    isPageView: true,
  });

  const { householdResource } = useSimpleHousehold();
  const { collection: goalCollection, setCollection: setGoalCollection } = useCollection();
  const [goalResource, setGoalResource] = useState();
  const [readOnly, setReadOnly] = useState(true);

  const dispatch = useBeaconDispatch();

  const getGoals = async () => {
    if (householdResource?.relationship('goals')) {
      try {
        const goals = await householdResource.relationship('goals').link('related').Get();
        setGoalCollection(goals);
        dispatch({
          type: 'setBeaconCount',
          beacon: 'goals',
          count: goals.resources().list().length,
        });
        if (goals.resources().list().length === 0) {
          addQuery({ createNewGoal: true });
        }
      } catch (e) {
        displayErrorMessage(e, 'Could not get household goals');
      }
    }
  };

  const toggleReadOnly = () => {
    setReadOnly(current => !current);
  };

  const handleAddGoalClick = () => {
    removeQuery(goalId, goalType);
    addQuery({ createNewGoal: true });
    logEvent('Client Goals Page: Clicked Add Goal Button');
  };

  const onSubmit = async (coll) => {
    removeQuery(goalType);
    addQuery({ goalId: coll.resources().head().id() });
    setReadOnly(true);
    try {
      await getGoals();
      displaySuccess('Successfully saved goal.');
      logEventSuccess('Client Goals Page: Clicked Submit on Goal');
    } catch (err) {
      displayErrorMessage(err, err.errors);
      logEventFailureWithError('Client Goals Page: Clicked Submit on Goal', err);
    }
  };

  const onDelete = async () => {
    removeQuery(goalId, goalType);
    setReadOnly(true);
    try {
      await getGoals();
      displaySuccess('Successfully removed goal.');
      logEventSuccess('Client Goals Page: Clicked Delete on Goal');
    } catch (err) {
      displayErrorMessage(err, err.errors);
      logEventFailureWithError('Client Goals Page: Clicked Delete on Goal', err);
    }
  };

  useEffect(() => {
    getGoals();
  }, [householdResource]);

  useEffect(() => {
    if (goalCollection) {
      if (!activeGoalId && selectedGoalType) {
        goalCollection.create({
          metaOverrideSet: GoalFormOverride,
          addToCollection: true,
        });
        const [firstGoal] = goalCollection.resources().list().reverse();
        firstGoal.attr('type', selectedGoalType).attr('percentComplete', 0);
        setGoalResource(firstGoal);
        toggleReadOnly();
      }
    }
  }, [goalCollection, activeGoalId, selectedGoalType]);

  useEffect(() => {
    if (goalCollection && !selectedGoalType) {
      let goalToDisplay;
      if (activeGoalId) {
        goalToDisplay = goalCollection.resources().list().find(res => res.id() === activeGoalId);
      } else {
        let goalList;
        goalList = goalCollection.resources().list().reverse().filter(goal => goal.attr('completed') !== 'true');
        if (goalList.length === 0) {
          goalList = goalCollection.resources().list().reverse().filter(goal => goal.attr('completed') === 'true');
        }
        [goalToDisplay] = goalList;
      }
      setGoalResource(goalToDisplay ? goalToDisplay.overrideMeta(readOnly ? ReadOnlyGoalOverride : GoalFormOverride) : null);
    }
  }, [goalCollection, activeGoalId, readOnly, selectedGoalType]);

  return (
    <div
      {...rootNodeProps}
      className={classNames(classes.root)}
    >
      <div className={classNames(classes.addGoalButtonContainer, {
        [classes.selectGoalHeader]: createNew,
      })}
      >
        <FDStack direction="column" alignItems="center" spacing={2}>
          <FDHeadlineText value="My Goals" />
          {createNew ? (
            <div className={classes.goalHeaderSubtitle}>
            Select a goal to begin
            </div>
          ) : (
            <FDPrimaryActionButton onClick={handleAddGoalClick} icon="add" label="Add Goal" />
          )}
        </FDStack>
      </div>
      <div className={classes.goalDetailContainer}>
        <div className={classes.goalSidebar}>
          {goalCollection && (
            <MyGoalsSidebar
              activeGoalId={goalResource?.id()}
              goals={goalCollection.resources().list().reverse()}
              updateGoalCollection={getGoals}
              createNew={createNew}
            />
          )}
        </div>
        <div className={classes.goalDetail}>
          {goalCollection && createNew && (
            <GoalTypeSelector goalCollection={goalCollection} />
          )}
          {goalResource && !createNew && (
            <GoalForm
              onSubmit={onSubmit}
              onDelete={onDelete}
              goalResource={goalResource}
              selectedGoalType={selectedGoalType}
              readOnly={readOnly}
              toggleReadOnly={toggleReadOnly}
              onMarkCompleted={getGoals}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Goals;
